import axios from 'axios';
// Use environment variable if available, otherwise use defaults based on environment
const getDefaultApiUrl = () => {
    // Use REACT_APP_API_URL if set (Webpack DefinePlugin injects this)
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    // Default to prod API in production, local in development
    const isProd = process.env.NODE_ENV === 'production';
    return isProd ? 'https://api.ksymmetry.app' : 'http://localhost:3001';
};
const ENV_CONFIG = {
    local: 'http://localhost:3001',
    dev: 'https://dev-api.ksymmetry.app',
    prod: 'https://api.ksymmetry.app',
};
const ENV_STORAGE_KEY = 'backoffice_environment';
class ApiClient {
    constructor() {
        this.isRefreshing = false;
        this.refreshSubscribers = [];
        // Load saved environment or use null to use default API URL
        this.currentEnvironment = this.getStoredEnvironment();
        this.client = axios.create({
            baseURL: this.getBaseURL(),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        this.setupInterceptors();
    }
    getStoredEnvironment() {
        const stored = localStorage.getItem(ENV_STORAGE_KEY);
        if (stored === 'local' || stored === 'prod' || stored === 'dev') {
            return stored;
        }
        return null;
    }
    getBaseURL() {
        // If environment is set via UI, use that
        if (this.currentEnvironment) {
            return ENV_CONFIG[this.currentEnvironment];
        }
        // Otherwise use default based on environment variable or NODE_ENV
        return getDefaultApiUrl();
    }
    setEnvironment(env) {
        this.currentEnvironment = env;
        localStorage.setItem(ENV_STORAGE_KEY, env);
        this.client.defaults.baseURL = this.getBaseURL();
        // Clear auth tokens when switching environments
        this.clearTokens();
        // Redirect to login
        window.location.href = '/login';
    }
    getCurrentEnvironment() {
        return this.currentEnvironment;
    }
    setupInterceptors() {
        // Request interceptor - add auth token
        this.client.interceptors.request.use((config) => {
            const tokens = this.getTokens();
            if (tokens?.accessToken && config.headers) {
                config.headers.Authorization = `Bearer ${tokens.accessToken}`;
            }
            return config;
        }, (error) => Promise.reject(error));
        // Response interceptor - handle 401 and refresh token
        this.client.interceptors.response.use((response) => response, async (error) => {
            const originalRequest = error.config;
            if (error.response?.status === 401 && !originalRequest._retry) {
                if (this.isRefreshing) {
                    return new Promise((resolve) => {
                        this.refreshSubscribers.push((token) => {
                            if (originalRequest.headers) {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                            }
                            resolve(this.client(originalRequest));
                        });
                    });
                }
                originalRequest._retry = true;
                this.isRefreshing = true;
                try {
                    const tokens = this.getTokens();
                    if (!tokens?.refreshToken) {
                        throw new Error('No refresh token available');
                    }
                    const response = await axios.post(`${this.getBaseURL()}/v1/admin/auth/refresh`, { refreshToken: tokens.refreshToken });
                    const newTokens = {
                        accessToken: response.data.data.tokens.accessToken,
                        refreshToken: response.data.data.tokens.refreshToken,
                    };
                    this.setTokens(newTokens);
                    this.refreshSubscribers.forEach((callback) => callback(newTokens.accessToken));
                    this.refreshSubscribers = [];
                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
                    }
                    return this.client(originalRequest);
                }
                catch (refreshError) {
                    this.clearTokens();
                    window.location.href = '/login';
                    return Promise.reject(refreshError);
                }
                finally {
                    this.isRefreshing = false;
                }
            }
            return Promise.reject(error);
        });
    }
    getTokens() {
        const tokensStr = localStorage.getItem('auth_tokens');
        return tokensStr ? JSON.parse(tokensStr) : null;
    }
    setTokens(tokens) {
        localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    }
    clearTokens() {
        localStorage.removeItem('auth_tokens');
    }
    getClient() {
        return this.client;
    }
    // Proxy methods for convenience
    get(url, config) {
        return this.client.get(url, config);
    }
    post(url, data, config) {
        return this.client.post(url, data, config);
    }
    put(url, data, config) {
        return this.client.put(url, data, config);
    }
    patch(url, data, config) {
        return this.client.patch(url, data, config);
    }
    delete(url, config) {
        return this.client.delete(url, config);
    }
    logout() {
        this.clearTokens();
    }
}
export const apiClient = new ApiClient();
export default apiClient.getClient();
